import { Space, Tabs } from 'antd'
import { useEffect, useState } from 'react'
import { alphabeticalSort } from 'shared'
import { useCheckPermissionsMap } from 'hooks/useCheckPermission'
import { CIMSTabsProps } from './types'

/**
 * a tab view where the correct tab from `props.tabs` is rendered when its
 * corresponding tab is clicked by the user
 *
 * @param props {@link CIMSTabsProps}
 */
export function CIMSTabs<KeyType extends string>({
  initialTab,
  tabs,
  sort,
  prefix,
}: CIMSTabsProps<KeyType>) {
  const [tab, setTab] = useState(initialTab)
  const [map] = useCheckPermissionsMap(
    tabs.map(({ permission, key }) => ({ key, permission: permission ?? true }))
  )
  const sortedTabs = sort ? tabs.sort(alphabeticalSort('label')) : tabs
  const tabPanes = sortedTabs.map(({ label, key, disabled }, i) => {
    const allowed = map.get(key)
    return (
      <Tabs.TabPane
        disabled={disabled || !allowed}
        key={key ?? i}
        tab={label}
      />
    )
  })
  const [component, setComponent] = useState<React.ReactNode>(null)
  useEffect(() => {
    const current = sortedTabs.find(({ key }) => key === tab)
    if (!current) return setComponent(<>Could not find tab by key: {tab}</>)
    return setComponent(
      typeof current.component === 'function'
        ? current.component(tab)
        : current.component
    )
  }, [sortedTabs, tab])

  return tabPanes.length ? (
    <>
      <Space size="small" direction="vertical" style={{ width: '100%' }}>
        {prefix}
        <Tabs
          defaultActiveKey={initialTab}
          type="card"
          hideAdd={true}
          onTabClick={(activeKey) => setTab(activeKey as KeyType)}
        >
          {tabPanes}
        </Tabs>
      </Space>
      {component}
    </>
  ) : (
    <>No Tab Panes were able to be rendered</>
  )
}
